import {
  BreakPointCondition,
  Checkbox,
  ErrorBoundary,
  Table,
  useBreakpointIf,
} from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Box, FlatList } from 'native-base';
import React, { useCallback } from 'react';
import { Dimensions, DimensionValue } from 'react-native';
import { useRecoilState } from 'recoil';

import {
  batchActionSelectedProductIds,
  batchActionState,
} from '../../../../../store/ProducManagement/atom';
import { BarSelectedItems } from '../BarSelectedItems';
import { BarWarningLimitReached } from '../BarWarningLimitReached';
import { TableHeadContainer, TableHeadItem, TableHeadItemRecurse } from '../TableHead';

import { ProductTableMobile } from './components/Mobile';
import { ProductTableTablet } from './components/Tablet';
import { ProductFieldProps } from './constants';

export interface WithProductId {
  productId: number | string;
  managerImage: string;
}

export interface IDynamicProductSearchItem<T> {
  item: T;
  selectedItems: string[];
  onSelect: (value: string) => void;
  onDetails: (productId: number, managerImage: string, productName: string) => void;
}

export interface DynamicProductTableProps<T extends WithProductId> {
  tableHeadItems: TableHeadItemRecurse[];
  mobileFields: ProductFieldProps<T>[];
  ProductItem: React.FC<IDynamicProductSearchItem<T>>;
  tabletColumns: number;
  tabletRows?: number;
  mobileColumns: number;
  mobileRows?: number;
  productType: 'funds' | 'fixedIncome';
  data: T[];
  loading: boolean;
  onDetails: (productId: number) => void;
}

const ItemSeparator = () => <Box height={Spacing['stack.nano']} />;

export const DynamicProductTable = <T extends WithProductId>({
  data = [],
  tableHeadItems,
  ProductItem,
  tabletColumns,
  tabletRows = 1,
  mobileColumns,
  mobileRows = 1,
  mobileFields,
  loading,
  productType,
  onDetails,
}: DynamicProductTableProps<T>) => {
  const columns = tableHeadItems.length + 1;
  const maxHeight = `${Dimensions.get('window').height - 295}px` as DimensionValue;
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const [, setBatchAction] = useRecoilState(batchActionState);
  const [batchActionSelectedIds, setBatchActionSelectedIds] = useRecoilState(
    batchActionSelectedProductIds
  );

  const isSelectAll = data.length > 0 && batchActionSelectedIds.length === data.length;

  const countSelected = batchActionSelectedIds.length;
  const limitSelectionFixedIncome = productType === 'fixedIncome' && countSelected > 20;

  const handleSelecteItems = useCallback(
    (value: string) => {
      if (value.includes('select-all')) {
        setBatchActionSelectedIds(isSelectAll ? [] : data.map((item) => item.productId.toString()));
      } else {
        setBatchActionSelectedIds((prev) => {
          if (prev.includes(value)) {
            return prev.filter((item) => item !== value);
          }
          return [...prev, value];
        });
      }
    },
    [data, isSelectAll, setBatchActionSelectedIds]
  );

  const renderTableHead = useCallback(
    () => (
      <TableHeadContainer px={Spacing['inline.xxxs']}>
        <TableHeadItem flex={0.5}>
          <Checkbox
            optionPadding={{ bottom: 0, right: 0 }}
            options={[{ label: '', value: 'select-all' }]}
            onChange={handleSelecteItems}
            testId="checkbox-select-all"
            selectedValues={isSelectAll ? ['select-all'] : []}
          />
        </TableHeadItem>
        {tableHeadItems.map((item, index) => (
          <TableHeadItem key={`${item.title}-${index}`} {...item} />
        ))}
      </TableHeadContainer>
    ),
    [tableHeadItems, handleSelecteItems, isSelectAll]
  );

  const renderItem = useCallback(
    ({ item }: { item: T }) => (
      <ProductItem
        item={item}
        selectedItems={batchActionSelectedIds}
        onSelect={handleSelecteItems}
        onDetails={onDetails}
      />
    ),
    [handleSelecteItems, onDetails]
  );

  const keyExtractor = useCallback((item: T) => {
    return String(item.productId);
  }, []);

  if (isMobile) {
    return (
      <ProductTableMobile
        data={data}
        isLoading={loading}
        fields={mobileFields}
        columns={mobileColumns}
        rows={mobileRows}
        selectedItems={batchActionSelectedIds}
        countSelected={countSelected}
        onSelect={handleSelecteItems}
        onBatchAction={(action) => setBatchAction(action)}
        handleSelecteItems={handleSelecteItems}
        isSelectAll={isSelectAll}
        onDetails={onDetails}
        productType={productType}
      />
    );
  }

  if (isTablet) {
    return (
      <ProductTableTablet
        data={data}
        isLoading={loading}
        fields={mobileFields}
        columns={tabletColumns}
        rows={tabletRows}
        selectedItems={batchActionSelectedIds}
        countSelected={countSelected}
        onSelect={handleSelecteItems}
        onBatchAction={(action) => setBatchAction(action)}
        handleSelecteItems={handleSelecteItems}
        productType={productType}
        isSelectAll={isSelectAll}
        onDetails={onDetails}
      />
    );
  }

  if (!data.length && !loading) {
    return (
      <Box py={{ sm: Spacing['inline.md'] }} width="full">
        <ErrorBoundary
          errorIconOrIllustration="NotEmptyIllustration"
          errorTitle="Nenhum resultado corresponde à sua pesquisa"
          errorMessage="Por favor, verifique o campo informado e tente novamente."
        />
      </Box>
    );
  }

  return (
    <Box overflow={'hidden'}>
      <Table isLoading={loading} cols={columns} rows={5}>
        {renderTableHead()}
        {countSelected > 1 && !limitSelectionFixedIncome && (
          <BarSelectedItems
            countSelected={countSelected}
            onBatchAction={(action) => setBatchAction(action)}
          />
        )}
        {limitSelectionFixedIncome && <BarWarningLimitReached />}
        <FlatList
          windowSize={5}
          maxToRenderPerBatch={5}
          updateCellsBatchingPeriod={50}
          initialNumToRender={10}
          removeClippedSubviews={true}
          data={data}
          contentContainerStyle={{ maxHeight }}
          showsVerticalScrollIndicator={false}
          onEndReachedThreshold={0.5}
          ItemSeparatorComponent={ItemSeparator}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          getItemLayout={(_, index) => ({
            length: 60,
            offset: 60 * index,
            index,
          })}
        />
      </Table>
    </Box>
  );
};
